/**
 * Thanh tab danh mục BĐS trên trang chủ — lọc featured hoặc tìm kiếm.
 *
 * Luồng dữ liệu:
 * - Nhận categories từ layout/header; ghép với tab "Tất cả".
 * - Đọc/ghi URL khác nhau tùy chế độ: home_category (trang chủ) vs category_id+search (tìm kiếm).
 * - handleSelect cập nhật searchParams → HomePage/HomeSearchResults phản ứng theo.
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getCategoryIcon } from '../../shared/utils/categoryIcon';
import { getCategoryName, getCategorySearchKeyword } from '../../shared/utils/categoryLabel';
import {
  HOME_CATEGORY_PARAM,
  hasActivePropertySearch,
} from '../../shared/utils/propertySearchParams';
import {
  getPagedScrollTarget,
  smoothScrollHorizontal,
} from '../../shared/utils/smoothHorizontalScroll';

const DEFAULT_TABS = [{ id: 'all', label: 'Tất cả', icon: '🏡' }];

export const HomeCategoryBar = ({ categories = [] }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const scrollRef = useRef(null);
  const cancelScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  /** true khi URL đang ở chế độ tìm kiếm (có query params tìm kiếm) */
  const isMapSearchView = hasActivePropertySearch(searchParams);

  /** Danh sách tab: mặc định + danh mục từ API */
  const tabs = [
    ...DEFAULT_TABS,
    ...categories.map((category) => ({
      id: String(category.id),
      label: getCategoryName(category),
      searchKeyword: getCategorySearchKeyword(category),
      icon: getCategoryIcon(category.icon),
    })),
  ];

  /** ID tab đang active — đọc từ param khác nhau tùy chế độ xem */
  const urlCategoryId = isMapSearchView
    ? searchParams.get('category_id') || 'all'
    : searchParams.get(HOME_CATEGORY_PARAM) || 'all';
  const [selectedTabId, setSelectedTabId] = useState(urlCategoryId);

  /** Đồng bộ state local khi URL thay đổi (back/forward, link ngoài) */
  useEffect(() => {
    setSelectedTabId(urlCategoryId);
  }, [urlCategoryId]);

  /** Cập nhật trạng thái nút cuộn trái/phải */
  const updateScrollState = () => {
    const element = scrollRef.current;

    if (!element) {
      return;
    }

    setCanScrollLeft(element.scrollLeft > 4);
    setCanScrollRight(element.scrollLeft + element.clientWidth < element.scrollWidth - 4);
  };

  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);

    return () => {
      window.removeEventListener('resize', updateScrollState);
      cancelScrollRef.current?.();
    };
  }, [categories]);

  /** Cuộn ngang thanh tab theo hướng */
  const scrollByAmount = (direction) => {
    const element = scrollRef.current;

    if (!element) {
      return;
    }

    cancelScrollRef.current?.();

    const targetLeft = getPagedScrollTarget(element, direction, 80);

    cancelScrollRef.current = smoothScrollHorizontal(element, targetLeft, {
      duration: 480,
      onComplete: updateScrollState,
    });
  };

  /**
   * Xử lý chọn tab danh mục:
   * - Trang chủ: ghi home_category, xóa params tìm kiếm.
   * - Chế độ tìm: ghi category_id + search keyword tương ứng.
   */
  const handleSelect = (tabId) => {
    setSelectedTabId(tabId);

    const params = new URLSearchParams(searchParams);

    if (!hasActivePropertySearch(searchParams)) {
      params.delete('category_id');
      params.delete('search');
      params.delete('page');

      if (tabId === 'all') {
        params.delete(HOME_CATEGORY_PARAM);
      } else {
        params.set(HOME_CATEGORY_PARAM, tabId);
      }

      setSearchParams(params);
      return;
    }

    const categorySearchTerms = tabs
      .filter((tab) => tab.id !== 'all')
      .map((tab) => tab.searchKeyword.toLowerCase());

    if (tabId === 'all') {
      params.delete('category_id');

      const currentSearch = (params.get('search') || '').trim().toLowerCase();

      if (categorySearchTerms.includes(currentSearch)) {
        params.delete('search');
      }
    } else {
      const tab = tabs.find((item) => item.id === tabId);

      params.set('category_id', tabId);

      if (tab?.searchKeyword) {
        params.set('search', tab.searchKeyword);
      }
    }

    params.delete(HOME_CATEGORY_PARAM);
    params.delete('page');
    setSearchParams(params);
  };

  return (
    <div className="home-category-bar">
      <div className="home-category-bar-inner">
        {canScrollLeft && (
          <button
            type="button"
            className="home-category-nav home-category-nav--prev"
            aria-label="Cuộn trái"
            onClick={() => scrollByAmount(-1)}
          >
            <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M20 8l-8 8 8 8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        <div
          className="home-category-scroll"
          ref={scrollRef}
          onScroll={updateScrollState}
        >
          {tabs.map((tab) => {
            const isActive = selectedTabId === tab.id;

            return (
              <button
                key={tab.id}
                type="button"
                className={`home-category-tab ${isActive ? 'is-active' : ''}`}
                aria-pressed={isActive}
                onClick={() => handleSelect(tab.id)}
              >
                <span className="home-category-tab-icon" aria-hidden="true">
                  {tab.icon}
                </span>
                <span className="home-category-tab-label">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {canScrollRight && (
          <button
            type="button"
            className="home-category-nav home-category-nav--next"
            aria-label="Cuộn phải"
            onClick={() => scrollByAmount(1)}
          >
            <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 8l8 8-8 8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};
